import { useCallback } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import { LoaderScreen } from '@/components/Elements/LoaderScreen';

import { Core } from 'cytoscape';

import {
  NodeType,
  MIN_ZOOM,
  MAX_ZOOM,
  NetworkData,
} from '@/features/connect-dots/types/cytoscape';
import { applyCytoscapeStyles } from '@/features/connect-dots/styles/networkStyles';
import { NodeTooltip } from '@/features/connect-dots/components/tooltips/NodeTooltip';
import { useCytoscapeEvents } from '@/features/connect-dots/hooks/useCytoscapeEvents';
import { useNodeTooltip } from '@/features/connect-dots/hooks/useNodeTooltip';
import { Toolbar } from '@/features/connect-dots/components/Toolbar/ToolBar';
import { setupMultiThemeSpiralLayout } from '@/features/connect-dots/styles/networkStyles';
import { NetworkHelpMenu } from '@/features/connect-dots/components/visualizer/NetworkHelpMenu';

interface NetworkContainerProps {
  elements: cytoscape.ElementDefinition[];
  isFilteringCompleted: boolean;
  filteredData: NetworkData;
  cytoscapeCoreRefObject: Core | null;
  setCytoscapeCoreRefObject: (cy: Core) => void;
  zoomLevel: number;
  setZoomLevel: (value: number) => void;
  nodeTypeFilters: { [key in NodeType]: boolean };
  updateNodeTypeFilter: (nodeType: NodeType, isVisible: boolean) => void;
}

export const NetworkContainer = ({
  elements,
  isFilteringCompleted,
  filteredData,
  cytoscapeCoreRefObject,
  setCytoscapeCoreRefObject,
  zoomLevel,
  setZoomLevel,
  nodeTypeFilters,
  updateNodeTypeFilter,
}: NetworkContainerProps) => {
  const { tooltipState, showTooltip, hideTooltip } = useNodeTooltip();

  const setCy = useCallback(
    (cyInstance: Core) => {
      if (!cyInstance || cytoscapeCoreRefObject === cyInstance) {
        return;
      }

      // 初期値を同期（クランプ）
      const initialZoom = Number(zoomLevel.toFixed(2));
      const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, initialZoom));
      setZoomLevel(clampedZoom);
      cyInstance.resize();
      cyInstance.center();

      // レイアウトの適用（描画が安定してから実行する）
      try {
        // cytoscape の内部処理やレイアウトが requestAnimationFrame を使って
        // フレーム処理を行うため、マウント直後に同期実行すると
        // 内部 state が未初期化で null 参照が発生する可能性がある。
        // そのため次のフレームで実行する。
        requestAnimationFrame(() => {
          try {
            setupMultiThemeSpiralLayout(cyInstance);
          } catch (err) {
            // レイアウト中の例外はログに残すが UI を壊さない
            // (ネイティブの cytoscape エラーがここで発生することがある)
            console.error(
              'Error in setupMultiThemeSpiralLayout (deferred):',
              err
            );
          }
        });
      } catch (error) {
        console.error('Error deferring setupMultiThemeSpiralLayout:', error);
      }

      applyCytoscapeStyles(cyInstance);

      setCytoscapeCoreRefObject(cyInstance);
    },
    [setCytoscapeCoreRefObject, cytoscapeCoreRefObject, zoomLevel, setZoomLevel]
  );

  useCytoscapeEvents({
    cytoscapeCoreRefObject,
    showTooltip,
    hideTooltip,
    zoomLevel,
    setZoomLevel,
  });

  return (
    <div
      className="size-full"
      style={{
        minHeight: '80vh',
        maxHeight: 'calc(100vh - 8px)',
        minWidth: '300px',
        maxWidth: '100%',
        position: 'relative', // ローディング表示のために必要
        overflow: 'hidden',
      }}
    >
      {!isFilteringCompleted && <LoaderScreen contained={true} />}
      {/* ツールバー */}
      <Toolbar
        cytoscapeCoreRefObject={cytoscapeCoreRefObject}
        filteredData={filteredData}
        nodeTypeFilters={nodeTypeFilters}
        updateNodeTypeFilter={updateNodeTypeFilter}
      />

      {/* ヘルプメニュー（右上） */}
      <div className="absolute right-4 top-2 z-40 rounded-full bg-rw-dark-gray/50">
        <NetworkHelpMenu />
      </div>

      <CytoscapeComponent
        elements={elements}
        style={{ width: '100%', height: 'calc(100vh - 8px)' }}
        panningEnabled={true}
        zoomingEnabled={true}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        boxSelectionEnabled={false}
        autoungrabify={false}
        cy={setCy}
      />

      {tooltipState && (
        <NodeTooltip
          tooltipRef={tooltipState.tooltipRef}
          content={tooltipState.content}
          position={tooltipState.position}
          visible={tooltipState.visible}
          onClose={hideTooltip}
          cytoscapeCoreRefObject={cytoscapeCoreRefObject}
        />
      )}
    </div>
  );
};
